import axios from ".";
import {
  Job,
  LanguageCode,
} from "../pages/playground/types";

// Phrasebook API Types
export enum PhraseType {
  TITLE = "TITLE",
  EDITORIAL_DESCRIPTION = "EDITORIAL_DESCRIPTION",
  SHORT_DESCRIPTION = "SHORT_DESCRIPTION",
  LONG_DESCRIPTION = "LONG_DESCRIPTION",
  BULLET_POINTS = "BULLET_POINTS",
  BULLET_POINTS_1 = "BULLET_POINTS_1",
  BULLET_POINTS_2 = "BULLET_POINTS_2",
  // Dynamic description slots (up to 15 total descriptions)
  DESCRIPTION_4 = "DESCRIPTION_4",
  DESCRIPTION_5 = "DESCRIPTION_5",
  DESCRIPTION_6 = "DESCRIPTION_6",
  DESCRIPTION_7 = "DESCRIPTION_7",
  DESCRIPTION_8 = "DESCRIPTION_8",
  DESCRIPTION_9 = "DESCRIPTION_9",
  DESCRIPTION_10 = "DESCRIPTION_10",
  DESCRIPTION_11 = "DESCRIPTION_11",
  DESCRIPTION_12 = "DESCRIPTION_12",
  DESCRIPTION_13 = "DESCRIPTION_13",
  DESCRIPTION_14 = "DESCRIPTION_14",
  DESCRIPTION_15 = "DESCRIPTION_15",
  // Dynamic bullet point slots (up to 15 total bullet points)
  BULLET_POINTS_3 = "BULLET_POINTS_3",
  BULLET_POINTS_4 = "BULLET_POINTS_4",
  BULLET_POINTS_5 = "BULLET_POINTS_5",
  BULLET_POINTS_6 = "BULLET_POINTS_6",
  BULLET_POINTS_7 = "BULLET_POINTS_7",
  BULLET_POINTS_8 = "BULLET_POINTS_8",
  BULLET_POINTS_9 = "BULLET_POINTS_9",
  BULLET_POINTS_10 = "BULLET_POINTS_10",
  BULLET_POINTS_11 = "BULLET_POINTS_11",
  BULLET_POINTS_12 = "BULLET_POINTS_12",
  BULLET_POINTS_13 = "BULLET_POINTS_13",
  BULLET_POINTS_14 = "BULLET_POINTS_14",
}

export enum PhraseStatus {
  UNREVIEWED = "UNREVIEWED",
  INTERNALLY_REVIEWED = "INTERNALLY_REVIEWED",
  EXTERNALLY_REVIEWED = "EXTERNALLY_REVIEWED",
}

export interface Phrase {
  id?: string;
  phrase: string;
  status: PhraseStatus | undefined;
  is_global?: boolean;
  source?: string; // "global" or "local"
  translation_status?: {
    [key in LanguageCode]: boolean;
  };
  brand_id?: string;
  job_id?: string;
  type?: PhraseType;
  order?: number;
  brand_ids?: string[];
  usage_count?: number;
  job_ids?: string[];
  is_matching_garment_type?: boolean;
  suitable_for_title?: boolean;
  suitable_for_description?: boolean;
  suitable_for_bullet_points?: boolean;
  in_batch?: boolean;
  in_global?: boolean;
}

export interface JobPhrase extends Phrase {
  job_id: string;
  type: PhraseType;
  order: number;
}

export interface BrandPhrase extends Phrase {
  brand_id: string;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pages: number;
}

export interface DataResult<T> {
  data: T[];
}

// Phrasebook API Functions

/**
 * Create a new phrase and associate it with a brand
 */
export const createBrandPhrase = async (data: {
  phrase: string;
  brand_id: string;
  is_global?: boolean;
  status?: PhraseStatus;
}) => {
  return axios.post<BrandPhrase>("/phrasebook/brand/phrase", data);
};

/**
 * Remove association between phrase and brand
 */
export const removeBrandPhrase = async (brandId: string, phraseId: string) => {
  return axios.delete(`/phrasebook/brand/${brandId}/phrase/${phraseId}`);
};

/**
 * Get all phrases associated with multiple brands and/or batch
 */
export const findPhrases = async ({
  brandIds,
  page = 1,
  limit = 10,
  query,
  batchId,
  garmentType,
  signal,
  suitableForTitle,
  suitableForDescription,
  suitableForBulletPoints,
}: {
  brandIds?: string[];
  page?: number;
  limit?: number;
  query?: string;
  batchId?: string;
  garmentType?: string;
  signal?: AbortSignal;
  suitableForTitle?: boolean;
  suitableForDescription?: boolean;
  suitableForBulletPoints?: boolean;
}) => {
  const params = new URLSearchParams();
  if (page) params.append("page", page.toString());
  if (limit) params.append("limit", limit.toString());
  if (query) params.append("query", query);

  return axios.post<PagedResult<BrandPhrase>>(
    `/phrasebook/phrases?${params.toString()}`,
    {
      brand_ids: brandIds,
      batch_id: batchId,
      garment_type: garmentType,
      suitable_for_title: suitableForTitle,
      suitable_for_description: suitableForDescription,
      suitable_for_bullet_points: suitableForBulletPoints,
    },
    {
      signal,
    }
  );
};

export const getJobsByIds = async ({
  jobIds,
  batchId,
}: {
  jobIds: string[];
  batchId: string;
}) => {
  return axios.post<DataResult<Job[]>>("/job/by-ids", {
    job_ids: jobIds,
    batch_id: batchId,
  });
};

/**
 * Get all brands that have at least one associated global phrase
 * @param organizationId - Optional organization ID to filter brands (for admin editing client batches)
 */
export const getBrandsWithGlobalPhrases = async (organizationId?: string) => {
  const params = new URLSearchParams();
  if (organizationId) {
    params.append("organization_id", organizationId);
  }
  const queryString = params.toString();
  return axios.get<{
    data: { id: string; name: string }[];
  }>(`/phrasebook/brands${queryString ? `?${queryString}` : ""}`);
};

/**
 * Create a new phrase and associate it with a job
 */
export const createJobPhrase = async (data: {
  phrase: string;
  job_id: string;
  type: PhraseType;
  order: number;
  is_global?: boolean;
  status?: PhraseStatus;
}) => {
  return axios.post<JobPhrase>("/phrasebook/job/phrase", data);
};

/**
 * Edit an existing phrase associated with a job
 */
export const updateJobPhrase = async (
  phraseId: string,
  data: {
    phrase: string;
    job_id: string;
    type: PhraseType;
    order: number;
    is_global?: boolean;
    status?: PhraseStatus;
  }
) => {
  return axios.put<JobPhrase>(`/phrasebook/job/phrase/${phraseId}`, data);
};

/**
 * Remove association between phrase and job
 */
export const removeJobPhrase = async (
  jobId: string,
  phraseId: string,
  phraseType: PhraseType
) => {
  return axios.delete(
    `/phrasebook/job/${jobId}/phrase/${phraseId}/type/${phraseType}`
  );
};

/**
 * Get all phrases associated with a job, optionally filtered by type
 */
export const getJobPhrases = async ({ jobId }: { jobId: string }) => {
  return axios.get(`/phrasebook/job/${jobId}/phrases`);
};

/**
 * Update the order of phrases for a specific job and type
 */
export const reorderJobPhrases = async (
  jobId: string,
  phraseType: PhraseType,
  phraseIds: string[]
) => {
  return axios.post(`/phrasebook/job/${jobId}/reorder/${phraseType}`, {
    phrase_ids: phraseIds,
  });
};

/**
 * Update the status of a single phrase
 */
export const updatePhraseStatus = async (
  phraseId: string,
  status: PhraseStatus
) => {
  return axios.put(
    `/phrasebook/phrase/${phraseId}/status?status_value=${status}`
  );
};

/**
 * Perform create, update, and delete operations for job phrases in a single transaction
 */
export const unifiedJobPhrasesOperations = async (data: {
  creates?: Array<{
    phrase: string;
    job_id: string;
    type: PhraseType;
    order: number;
    is_global?: boolean;
    status?: PhraseStatus;
  }>;
  updates?: Array<{
    phrase_id: string;
    phrase: string;
    job_id: string;
    type: PhraseType;
    order: number;
    is_global?: boolean;
    status?: PhraseStatus;
  }>;
  deletes?: Array<{
    job_id: string;
    phrase_id: string;
    type: PhraseType;
  }>;
}) => {
  return axios.put(`/phrasebook/job/phrases/bulk`, data);
};

/**
 * Update the status of multiple phrases at once
 */
export const updatePhrasesStatusBulk = async (
  phraseIds: string[],
  status: PhraseStatus
) => {
  return axios.put(`/phrasebook/phrases/status/bulk`, {
    phrase_ids: phraseIds,
    status: status,
  });
};

/**
 * Update the global flag of a single phrase
 */
export const updatePhraseGlobalStatus = async (
  phraseId: string,
  isGlobal: boolean
) => {
  return axios.put(
    `/phrasebook/phrase/${phraseId}/global?is_global=${isGlobal}`
  );
};

/**
 * Update the global flag of multiple phrases at once
 */
export const updatePhrasesGlobalBulk = async (
  phraseIds: string[],
  isGlobal: boolean
) => {
  return axios.put(`/phrasebook/phrases/global/bulk`, {
    phrase_ids: phraseIds,
    is_global: isGlobal,
  });
};

/**
 * Search for jobs with phrases matching provided keywords
 *
 * This search can be performed within the current batch or across the entire brand
 */
export const keywordsSearch = async ({
  query,
  batchId,
  scope = "brand",
  signal,
}: {
  query: string;
  batchId: string;
  scope?: "batch" | "brand";
  signal?: AbortSignal;
}) => {
  return axios.post<DataResult<Job>>(
    "phrasebook/keywords-search",
    {
      query,
      batch_id: batchId,
      scope,
    },
    {
      signal,
    }
  );
};
